// The reading experience: paginates published chapters, mounts StPageFlip for
// realistic page turns, wires the TOC drawer, pagination controls, reading
// preferences, writing-progress banner, and per-reader resume.

import { PageFlip } from "page-flip";
import { api, BookView, ChapterContent, Me } from "./api";
import { renderChapter } from "./render";
import { paginate, PageSize } from "./paginate";
import { icons } from "./icons";
import {
  currentTheme,
  toggleTheme,
  getReadSize,
  getReadLeading,
  applyReadPrefs,
} from "./theme";

interface PageMap {
  chapterId: string;
  chapterIndex: number;
  pageInChapter: number;
}

export async function mountReader(root: HTMLElement, slug: string, me: Me) {
  root.innerHTML = `
    <div class="shell">
      ${topbar(me)}
      <div class="reader" id="reader">
        <button class="nav-arrow left" id="navPrev" aria-label="Previous page">${icons.left}</button>
        <div class="book-stage"><div id="flip"></div></div>
        <button class="nav-arrow right" id="navNext" aria-label="Next page">${icons.right}</button>
        <div class="reader-controls">
          <button class="iconbtn" id="ctlPrev">${icons.left}</button>
          <input type="range" class="scrubber" id="scrub" min="0" max="0" value="0" />
          <span class="pageinfo" id="pageinfo">—</span>
          <button class="iconbtn" id="ctlNext">${icons.right}</button>
        </div>
        ${prefsPopover()}
      </div>
    </div>
    ${drawer()}
    <div class="scrim" id="scrim"></div>
  `;

  const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;

  // --- load content -----------------------------------------------------------
  const reader = $("reader");
  setLoading(reader, "Opening the book…");

  let bv: BookView;
  try {
    bv = await api.getBook(slug);
  } catch {
    setLoading(reader, "This book isn’t available yet.");
    return;
  }

  // Fetch full content of every published chapter.
  const chapters: ChapterContent[] = [];
  for (const meta of bv.chapters) {
    try {
      chapters.push(await api.getChapter(slug, meta.slug));
    } catch {
      /* skip unreadable chapter */
    }
  }

  // --- pagination + flip ------------------------------------------------------
  let flip: PageFlip | null = null;
  let pageMaps: PageMap[] = [];
  let chapterStartPage: Record<number, number> = {};

  function computeSize(): PageSize {
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    const height = Math.min(vh - 170, 760);
    const portrait = vw < 880;
    const width = portrait
      ? Math.min(vw - 60, height * 0.66)
      : Math.min((vw - 120) / 2, height * 0.68);
    return { width: Math.round(width), height: Math.round(height) };
  }

  function buildPages(size: PageSize): HTMLElement[] {
    pageMaps = [];
    chapterStartPage = {};
    const pages: HTMLElement[] = [];

    // Cover.
    pages.push(coverPage(bv));
    pageMaps.push({ chapterId: "", chapterIndex: -1, pageInChapter: 0 });

    chapters.forEach((ch, ci) => {
      const blocks = renderChapter(ch.title, `Chapter ${ci + 1}`, ch.blocks);
      const inners = paginate(blocks, size);
      chapterStartPage[ci] = pages.length;
      inners.forEach((inner, pi) => {
        pages.push(wrapPage(inner, pages.length));
        pageMaps.push({ chapterId: ch.id, chapterIndex: ci, pageInChapter: pi });
      });
    });

    // Keep an even count so spreads close nicely.
    if (pages.length % 2 !== 0) {
      const blank = document.createElement("div");
      blank.className = "page";
      blank.appendChild(Object.assign(document.createElement("div"), { className: "page-inner" }));
      pages.push(blank);
      pageMaps.push({ chapterId: "", chapterIndex: -1, pageInChapter: 0 });
    }
    return pages;
  }

  function render() {
    const size = computeSize();
    const stage = document.querySelector<HTMLElement>(".book-stage")!;
    stage.style.setProperty("--page-w", `${size.width}px`);
    stage.style.setProperty("--page-h", `${size.height}px`);

    if (flip) {
      try { flip.destroy(); } catch {}
      flip = null;
    }

    // StPageFlip mutates its host on init (adds .stf__parent/.stf__wrapper and
    // rewrites its children), so the original #flip node can't be reused on a
    // re-render — re-querying it returned null and threw. Rebuild a clean host
    // inside the stable .book-stage each time (e.g. on font-size/resize).
    stage.innerHTML = '<div id="flip"></div>';
    const host = $("flip");

    const pages = buildPages(size);

    flip = new PageFlip(host, {
      width: size.width,
      height: size.height,
      size: "fixed",
      drawShadow: true,
      maxShadowOpacity: 0.5,
      showCover: true,
      flippingTime: 700,
      usePortrait: true,
      autoSize: false,
      mobileScrollSupport: true,
    } as any);

    flip.loadFromHTML(pages as unknown as HTMLElement[]);
    $("scrub").setAttribute("max", String(flip.getPageCount() - 1));

    flip.on("flip", (e: any) => onPage(e.data as number));
    onPage(flip.getCurrentPageIndex());
  }

  // --- progress + controls ----------------------------------------------------
  let saveTimer: number | undefined;
  function onPage(idx: number) {
    if (!flip) return;
    const total = flip.getPageCount();
    $("pageinfo").textContent = idx === 0 ? "Cover" : `p. ${idx} / ${total - 1}`;
    ($("scrub") as HTMLInputElement).value = String(idx);
    highlightToc(pageMaps[idx]?.chapterIndex ?? -1);

    if (me.authenticated) {
      const map = pageMaps[idx];
      if (map && map.chapterId) {
        const percent = total > 1 ? (idx / (total - 1)) * 100 : 0;
        window.clearTimeout(saveTimer);
        saveTimer = window.setTimeout(() => {
          api.saveProgress(slug, map.chapterId, map.pageInChapter, percent).catch(() => {});
        }, 900);
      }
    }
  }

  function highlightToc(ci: number) {
    document.querySelectorAll(".toc-item").forEach((el) => {
      el.classList.toggle("active", Number((el as HTMLElement).dataset.ci) === ci);
    });
  }

  // --- TOC + progress banner --------------------------------------------------
  function fillDrawer() {
    const p = bv.progress;
    const updated = p.last_updated ? timeAgo(new Date(p.last_updated)) : "—";
    $("progressCard").innerHTML = `
      <div class="label">Writing progress</div>
      <div class="headline">Chapter ${p.chapters_published} of ${p.chapters_planned} · ${Math.round(p.percent)}% drafted</div>
      <div class="bar"><span style="width:${Math.min(p.percent, 100)}%"></span></div>
      <div class="meta"><span>${p.total_words.toLocaleString()} words</span><span>updated ${updated}</span></div>
    `;
    const toc = $("toc");
    toc.innerHTML = "";
    bv.chapters.forEach((c, ci) => {
      const b = document.createElement("button");
      b.className = "toc-item";
      b.dataset.ci = String(ci);
      b.innerHTML = `<span class="n">${ci + 1}</span><span class="t">${escapeHtml(c.title)}</span>`;
      b.onclick = () => {
        const start = chapterStartPage[ci];
        if (flip && start != null) flip.flip(start);
        closeDrawer();
      };
      toc.appendChild(b);
    });
  }

  function openDrawer() { $("drawer").classList.add("open"); $("scrim").classList.add("open"); }
  function closeDrawer() { $("drawer").classList.remove("open"); $("scrim").classList.remove("open"); }

  // --- events -----------------------------------------------------------------
  $("navPrev").onclick = () => flip?.flipPrev();
  $("navNext").onclick = () => flip?.flipNext();
  $("ctlPrev").onclick = () => flip?.flipPrev();
  $("ctlNext").onclick = () => flip?.flipNext();
  ($("scrub") as HTMLInputElement).oninput = (e) =>
    flip?.flip(Number((e.target as HTMLInputElement).value));

  $("btnToc").onclick = openDrawer;
  $("drawerClose").onclick = closeDrawer;
  $("scrim").onclick = closeDrawer;

  $("btnTheme").onclick = () => { toggleTheme(); syncThemeIcon(); };
  $("btnPrefs").onclick = () => $("prefs").classList.toggle("open");
  if (me.is_admin) $("btnEdit").onclick = () => (location.hash = "#/admin");

  // reading prefs steppers
  bindStepper("sizeDown", "sizeUp", () => getReadSize(), (v) => { applyReadPrefs(v, getReadLeading()); debouncedRender(); }, 15, 26, 1, "sizeVal", "px");
  bindStepper("leadDown", "leadUp", () => getReadLeading(), (v) => { applyReadPrefs(getReadSize(), v); debouncedRender(); }, 1.3, 2.0, 0.1, "leadVal", "");

  document.addEventListener("keydown", (e) => {
    if ((e.target as HTMLElement)?.tagName === "INPUT") return;
    if (e.key === "ArrowLeft") flip?.flipPrev();
    if (e.key === "ArrowRight") flip?.flipNext();
    if (e.key === "t") openDrawer();
  });

  let rt: number | undefined;
  function debouncedRender() {
    window.clearTimeout(rt);
    rt = window.setTimeout(() => { render(); applyResume(); }, 250);
  }
  window.addEventListener("resize", debouncedRender);

  function applyResume() {
    if (!flip || !bv.resume) return;
    const ci = bv.chapters.findIndex((c) => c.id === bv.resume!.chapter_id);
    if (ci >= 0 && chapterStartPage[ci] != null) {
      const target = chapterStartPage[ci] + bv.resume.page_in_chapter;
      flip.flip(Math.min(target, flip.getPageCount() - 1));
    }
  }

  // --- go ---------------------------------------------------------------------
  applyReadPrefs();
  syncThemeIcon();
  render();
  fillDrawer();
  applyResume();
  clearLoading(reader);

  function syncThemeIcon() {
    $("btnTheme").innerHTML = currentTheme() === "dark" ? icons.sun : icons.moon;
  }
}

// ---------------------------------------------------------------- view helpers
function topbar(me: Me): string {
  return `
    <div class="topbar">
      <button class="iconbtn" id="btnToc" title="Contents (t)">${icons.menu}</button>
      <div class="brand"><span class="emoji">📖</span><span class="full">book</span></div>
      <div class="spacer"></div>
      ${me.is_admin ? `<button class="iconbtn" id="btnEdit" title="Write">${icons.edit}</button>` : ""}
      <button class="iconbtn" id="btnPrefs" title="Reading options">${icons.text}</button>
      <button class="iconbtn" id="btnTheme" title="Toggle theme">${icons.moon}</button>
    </div>`;
}

function drawer(): string {
  return `
    <aside class="drawer" id="drawer">
      <header><h2>Contents</h2><button class="iconbtn" id="drawerClose">${icons.close}</button></header>
      <div class="progress-card" id="progressCard"></div>
      <div class="toc" id="toc"></div>
    </aside>`;
}

function prefsPopover(): string {
  return `
    <div class="prefs" id="prefs">
      <div class="row"><span class="name">Text size</span>
        <span class="stepper"><button id="sizeDown">−</button><span id="sizeVal"></span><button id="sizeUp">+</button></span>
      </div>
      <div class="row"><span class="name">Line spacing</span>
        <span class="stepper"><button id="leadDown">−</button><span id="leadVal"></span><button id="leadUp">+</button></span>
      </div>
    </div>`;
}

function coverPage(bv: BookView): HTMLElement {
  const page = document.createElement("div");
  page.className = "page cover";
  page.setAttribute("data-density", "hard");
  const inner = document.createElement("div");
  inner.className = "page-inner";
  inner.innerHTML = `
    <div class="flourish">❦</div>
    <div class="title">${escapeHtml(bv.book.title)}</div>
    ${bv.book.subtitle ? `<div class="subtitle">${escapeHtml(bv.book.subtitle)}</div>` : ""}
    ${bv.book.author_name ? `<div class="author">${escapeHtml(bv.book.author_name)}</div>` : ""}
  `;
  page.appendChild(inner);
  return page;
}

function wrapPage(inner: HTMLElement, displayIdx: number): HTMLElement {
  const page = document.createElement("div");
  page.className = "page";
  page.appendChild(inner);
  const num = document.createElement("div");
  num.className = "page-num";
  num.textContent = String(displayIdx);
  page.appendChild(num);
  return page;
}

function bindStepper(
  downId: string,
  upId: string,
  get: () => number,
  set: (v: number) => void,
  min: number,
  max: number,
  step: number,
  valId: string,
  unit: string,
) {
  const show = () => {
    const el = document.getElementById(valId);
    if (el) el.textContent = `${Number(get().toFixed(1))}${unit}`;
  };
  show();
  const clamp = (v: number) => Math.min(max, Math.max(min, Number(v.toFixed(2))));
  document.getElementById(downId)!.onclick = () => { set(clamp(get() - step)); show(); };
  document.getElementById(upId)!.onclick = () => { set(clamp(get() + step)); show(); };
}

// Non-destructive loading state: overlays the reader instead of replacing its
// innerHTML, so the #flip host and controls built by mountReader survive until
// render() repopulates them. (Wiping the shell here used to null out #flip and
// throw inside render(), leaving the spinner up forever.)
function setLoading(el: HTMLElement, msg: string) {
  let overlay = el.querySelector<HTMLElement>("#readerLoading");
  if (!overlay) {
    overlay = document.createElement("div");
    overlay.id = "readerLoading";
    overlay.className = "reader-loading";
    el.appendChild(overlay);
  }
  overlay.innerHTML = `<div class="center-msg"><div><div class="spinner"></div>${escapeHtml(msg)}</div></div>`;
}

function clearLoading(el: HTMLElement) {
  el.querySelector("#readerLoading")?.remove();
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string),
  );
}

function timeAgo(d: Date): string {
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 3600) return `${Math.max(1, Math.round(s / 60))}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}
